import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import { PwaInstallButton } from "./PwaInstallButton";

interface Props {
  /** "landing": tokens cream/ink (imune ao dark). "app": tokens neutral (dark-aware). */
  variant: "landing" | "app";
}

const DISMISS_KEY: Record<Props["variant"], string> = {
  landing: "taskletto_pwa_banner_landing",
  app: "taskletto_pwa_banner_app",
};

/**
 * Faixa flutuante e dispensável convidando a instalar o app. Aparece só quando
 * o app é instalável e some para sempre (por variante) ao ser fechada.
 */
export function PwaInstallBanner({ variant }: Props) {
  const { canInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(
    () =>
      typeof localStorage !== "undefined" &&
      localStorage.getItem(DISMISS_KEY[variant]) === "1",
  );
  const [shown, setShown] = useState(false);

  // Anima a entrada só depois de montar (slide-up suave).
  useEffect(() => {
    if (canInstall && !dismissed) {
      const t = setTimeout(() => setShown(true), 50);
      return () => clearTimeout(t);
    }
    setShown(false);
  }, [canInstall, dismissed]);

  if (!canInstall || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY[variant], "1");
    setDismissed(true);
  };

  const isLanding = variant === "landing";

  const card = isLanding
    ? "bg-cream/95 border-2 border-ink/10 text-ink"
    : "bg-white/95 dark:bg-neutral-100 border border-neutral-200 text-neutral-900";
  const iconWrap = isLanding
    ? "bg-primary-500 text-ink"
    : "bg-primary-50 text-primary-600";
  const sub = isLanding ? "text-ink/60" : "text-neutral-500";
  const installBtn = isLanding
    ? "bg-ink text-cream hover:-translate-y-0.5"
    : "bg-primary-600 text-white hover:bg-primary-700";
  const closeBtn = isLanding
    ? "text-ink/40 hover:text-ink hover:bg-ink/5"
    : "text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100";

  return createPortal(
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-md px-1 transition-all duration-300 ${
        shown ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
      }`}
      style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div
        className={`flex items-center gap-3 rounded-2xl shadow-xl backdrop-blur-md px-3.5 py-3 ${card}`}
      >
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${iconWrap}`}
        >
          <Download size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight">Instale o Taskletto</p>
          <p className={`text-xs leading-tight mt-0.5 ${sub}`}>
            Acesse direto da tela inicial, sem abrir o navegador.
          </p>
        </div>
        <PwaInstallButton
          className={`shrink-0 px-3.5 py-2 rounded-full text-xs font-bold transition-all cursor-pointer ${installBtn}`}
        >
          Instalar
        </PwaInstallButton>
        <button
          onClick={dismiss}
          aria-label="Agora não"
          className={`shrink-0 p-1.5 rounded-lg transition-colors cursor-pointer ${closeBtn}`}
        >
          <X size={16} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
